/**
 * Findings, in the header — an icon button carrying a dot, opening a popover with the detail
 * the old `FindingsBadge` pill used to print inline (`⚠ 2 findings`).
 *
 * Replaces `FindingsBadge.tsx`. The count itself is unchanged — this still reads the same
 * `useFindingsSummary(selectedId)` result `App.tsx` already fetches for the rail badge, so
 * the rail, the header and this popover can never disagree — but the number now lives behind
 * a click instead of spelled out in the chrome, and the popover carries three figures the
 * pill never had room for: overdue, and the domains actually carrying the backlog.
 *
 * `ic-alert` (the triangle) rather than the bell's `ic-bell` or Compliance's `ic-shield` — see
 * `App.tsx`'s header-row comment for why the three controls deliberately do not rhyme.
 *
 * CLICK OPENS A POPOVER, IT NO LONGER NAVIGATES DIRECTLY. The old pill's `onClick` was the
 * same `setTab('findings')` the rail's own entry calls; a bare icon with no visible number
 * cannot make that same promise; unlike a labelled pill, and an operator would not know an
 * icon that quiet was clickable at all, let alone that it goes straight to another page. The
 * popover answers "what's in there" first; `onOpen` — still that identical `setTab` — is now
 * the explicit "Open Findings" action at the bottom of it.
 *
 * Colour follows severity exactly as the old pill did: danger only when something HIGH is
 * open, warn otherwise, muted at zero — so a header with three postures (alarms, findings,
 * compliance) does not spend red on everything and teach the operator to ignore it.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { domainLabel } from '@/lib/compliance-labels';
import type { FindingsSummary } from '@/lib/api/sites';
import { HeaderStatButton, type StatTone } from './HeaderStatButton';
import { Icon } from './Icon';

/*
 * NOT `DOMAINS` FROM THE ENGINE, and this is load-bearing rather than a style choice.
 *
 * This component is in the app header, so it is EAGER — it is in the entry chunk on every
 * cold load. `DOMAINS` is exported from `compliance/standards/index.ts`, which imports all
 * thirty-eight standards to build `STANDARDS` and `ALL` beside it, and there is no deeper
 * module holding the domain metadata on its own. Importing it here put 109 checks in front of
 * the sign-in screen and took the entry chunk from 181.8 KB gzipped to 242.8 KB, failing a
 * 200 KB budget that exists for exactly that reason.
 *
 * `compliance-labels.ts` carries the names instead, transcribed and guarded by a test that
 * imports the engine — which a test may do, since tests are never bundled.
 */

export function FindingsButton({
  summary,
  loading,
  siteName,
  onOpen,
  className,
}: {
  /** `useFindingsSummary(selectedId).data` — the same object the rail badge reads. */
  summary: FindingsSummary | undefined;
  loading: boolean;
  /** For the panel heading. Undefined when no site is selected, in which case this renders
      nothing — same rule `ComplianceButton` follows. */
  siteName: string | undefined;
  onOpen: () => void;
  className?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  /* Dismiss without forcing focus anywhere — the outside click already moved it. */
  const dismiss = useCallback((): void => setOpen(false), []);
  /* Dismiss AND return focus to the trigger — Escape, and the "Open Findings" action,
     mirroring `HeaderScope.tsx`'s `close()`. */
  const close = useCallback((): void => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) dismiss();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, dismiss, close]);

  /* THE ICON ITSELF, QUIET, WHILE THE COUNT LOADS (2026-10-08) — not a grey block. A block in the
     header's row of icons read as a missing control for the moment it showed (browser check,
     Alarms first paint), and then the triangle popped in over it. The icon is the same shape and
     size either way; only the dot waits for the number. */
  if (loading) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--muted-fg)]"
        style={{ width: 'var(--header-ctl-h)', height: 'var(--header-ctl-h)' }}
        aria-busy="true"
        aria-label="Findings: loading"
      >
        <Icon name="ic-alert" size={16} />
      </span>
    );
  }
  /* Nothing rather than a placeholder — same rule `ComplianceButton` follows. A findings
     icon with no site selected reads as a fetch that returned zero, not as "pick a site". */
  if (!siteName || !summary) return null;

  const { unresolved, high, overdue, byDomain } = summary;
  const tone: StatTone = high > 0 ? 'danger' : unresolved > 0 ? 'warn' : 'muted';

  /* The domains actually carrying the backlog, largest first. The pill never had room for
     this; the popover does, and it is the one thing "2 findings" could not tell an operator
     about where to start. */
  const topDomains = Object.entries(byDomain)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="relative" ref={wrap}>
      <HeaderStatButton
        ref={triggerRef}
        icon="ic-alert"
        dot
        count={unresolved}
        tone={tone}
        label="Findings"
        detail={`${siteName}: ${
          unresolved
            ? `${unresolved} unresolved finding${unresolved === 1 ? '' : 's'}${high > 0 ? `, ${high} high-priority` : ''}`
            : 'nothing open'
        }`}
        open={open}
        onClick={() => setOpen((v) => !v)}
        className={className}
      />

      {open && (
        <div
          role="dialog"
          aria-label={`Findings for ${siteName}`}
          className="lm-overlay absolute right-0 top-[calc(100%+8px)] z-50 overflow-hidden"
          style={{ width: 268 }}
        >
          <div className="p-[13px_14px]">
            <div
              className="truncate text-[13px] font-semibold"
              style={{ color: 'var(--muted-fg)' }}
              title={siteName}
            >
              Findings — {siteName}
            </div>

            {/* GUIDELINES §2: a card that cannot be answered is omitted, not em-dashed — so
                every line below is conditional on the number it carries being non-zero. */}
            {unresolved === 0 ? (
              <p className="mt-[10px] text-[12.5px]" style={{ color: 'var(--muted-fg)' }}>
                Nothing open.
              </p>
            ) : (
              <div className="mt-[10px] flex flex-col gap-[6px]">
                <Row label="Unresolved" value={unresolved} />
                {high > 0 && <Row label="High-priority" value={high} colour="var(--danger)" />}
                {overdue > 0 && <Row label="Overdue" value={overdue} colour="var(--warn)" />}
              </div>
            )}

            {topDomains.length > 0 && (
              <div className="mt-[12px] flex flex-col gap-[4px]">
                <div
                  className="text-[12px] font-semibold"
                  style={{ color: 'var(--muted-fg)' }}
                >
                  Top domains
                </div>
                {topDomains.map(([id, n]) => (
                  <div key={id} className="flex items-center justify-between gap-[8px] text-[12px]">
                    <span className="truncate" style={{ color: 'var(--fg)' }}>
                      {domainLabel(id)}
                    </span>
                    <span className="lm-num shrink-0 font-semibold" style={{ color: 'var(--muted-fg)' }}>
                      {n}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              close();
              onOpen();
            }}
            className="lm-hov flex w-full items-center justify-between gap-[8px] p-[11px_14px] text-left text-[12.5px] font-semibold"
            style={{ borderTop: '1px solid var(--border)', color: 'var(--fg)' }}
          >
            Open Findings
            <Icon name="ic-chevright" size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

/** One `label · value` line inside the panel. `colour` tints the figure only — the label
    stays `--fg`, so severity reads from the number, not from a wall of red text. */
function Row({ label, value, colour }: { label: string; value: number; colour?: string }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-[8px] text-[12.5px]">
      <span style={{ color: 'var(--fg)' }}>{label}</span>
      <span className="lm-num font-bold" style={{ color: colour ?? 'var(--fg)' }}>
        {value}
      </span>
    </div>
  );
}
